import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { axiosInstance } from "../App";
import {
  Users,
  Filter,
  UserX,
  Building2,
  Clock,
  AlertCircle,
  ArrowLeft,
  Activity,
  RefreshCw
} from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";

function ActiveUsers() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeUsers, setActiveUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [companyFilter, setCompanyFilter] = useState("all");
  const [kickDialogOpen, setKickDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    fetchActiveUsers();
  }, [companyFilter]);

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        fetchActiveUsers();
      }, 10000); // Refresh every 10 seconds

      return () => clearInterval(interval);
    }
  }, [autoRefresh, companyFilter]);

  const fetchCompanies = async () => {
    try {
      const response = await axiosInstance.get("/supervisor/companies");
      setCompanies(response.data);
    } catch (error) {
      console.error("Failed to fetch companies:", error);
    }
  };

  const fetchActiveUsers = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (companyFilter !== "all") {
        params.append("company_id", companyFilter);
      }

      const response = await axiosInstance.get(
        `/supervisor/active-users?${params.toString()}`
      );
      
      setActiveUsers(response.data);
    } catch (error) {
      console.error("Failed to fetch active users:", error);
      toast.error("Aktif kullanıcılar yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  const handleKickUser = async () => {
    if (!selectedUser) return;

    try {
      await axiosInstance.post(
        `/supervisor/active-users/${selectedUser.user_id}/kick`
      );
      
      toast.success(`${selectedUser.user_name} başarıyla atıldı`);
      setKickDialogOpen(false);
      setSelectedUser(null);
      fetchActiveUsers();
    } catch (error) {
      console.error("Failed to kick user:", error);
      toast.error(error.response?.data?.detail || "Kullanıcı atılamadı");
    }
  };

  const openKickDialog = (user) => {
    setSelectedUser(user);
    setKickDialogOpen(true);
  };

  const getRoleBadge = (role) => {
    const config = {
      supervisor: { label: "Supervisor", color: "bg-purple-100 text-purple-800" },
      admin: { label: "Admin", color: "bg-red-100 text-red-800" },
      manager: { label: "Müdür", color: "bg-blue-100 text-blue-800" },
      engineer: { label: "Mühendis", color: "bg-cyan-100 text-cyan-800" },
      personnel: { label: "Personel", color: "bg-gray-100 text-gray-800" },
      member: { label: "Personel", color: "bg-gray-100 text-gray-800" }
    };
    const roleConfig = config[role] || { label: role, color: "bg-gray-100 text-gray-800" };
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded ${roleConfig.color}`}>
        {roleConfig.label}
      </span>
    );
  };

  const formatDate = (isoDate) => {
    const date = new Date(isoDate);
    return new Intl.DateTimeFormat('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    }).format(date);
  };

  const getTimeSinceLogin = (loginDate) => {
    const now = new Date();
    const login = new Date(loginDate);
    const diffMs = now - login;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return "Az önce";
    if (diffMins < 60) return `${diffMins} dakika önce`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours} saat önce`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} gün önce`;
  };

  const getCompanyName = (companyId) => {
    if (!companyId) return "Sistem";
    const company = companies.find(c => c.id === companyId);
    return company?.name || companyId;
  };

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate("/supervisor")}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Geri
            </Button>
          </div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Users className="h-8 w-8" />
            Aktif Kullanıcılar
          </h1>
          <p className="text-gray-500 mt-1">Şu anda sistemde olan kullanıcıları görüntüleyin ve yönetin</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setAutoRefresh(!autoRefresh);
              toast.success(autoRefresh ? "Otomatik yenileme kapatıldı" : "Otomatik yenileme açıldı");
            }}
            className={autoRefresh ? "bg-green-50" : ""}
          >
            <Activity className={`h-4 w-4 mr-2 ${autoRefresh ? "animate-pulse" : ""}`} />
            {autoRefresh ? "Otomatik Yenileme Açık" : "Otomatik Yenileme Kapalı"}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchActiveUsers}
            disabled={loading}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Yenile
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Toplam Aktif Kullanıcı</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-blue-600">{activeUsers.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Aktif Firmalar</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-green-600">
              {new Set(activeUsers.filter(u => u.company_id).map(u => u.company_id)).size}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500">Sistem Kullanıcıları</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-purple-600">
              {activeUsers.filter(u => !u.company_id).length}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Filter className="h-5 w-5" />
            Filtreler
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Firma</label>
              <Select value={companyFilter} onValueChange={setCompanyFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Tüm Firmalar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm Firmalar</SelectItem>
                  {companies.map((company) => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Active Users List */}
      <Card>
        <CardHeader>
          <CardTitle>
            Aktif Kullanıcılar ({activeUsers.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
              <p className="mt-4 text-gray-500">Yükleniyor...</p>
            </div>
          ) : activeUsers.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">Aktif kullanıcı bulunamadı</p>
            </div>
          ) : (
            <div className="space-y-3">
              {activeUsers.map((user) => (
                <div
                  key={user.id}
                  className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-2">
                        <h3 className="font-semibold text-lg">{user.user_name}</h3>
                        {getRoleBadge(user.user_role)}
                        {user.company_name && (
                          <span className="px-2 py-1 text-xs bg-orange-100 text-orange-800 rounded flex items-center gap-1">
                            <Building2 className="h-3 w-3" />
                            {user.company_name}
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mb-2">{user.user_email}</p>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          Giriş: {formatDate(user.login_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <Activity className="h-3 w-3" />
                          Son Aktivite: {getTimeSinceLogin(user.last_activity)}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => openKickDialog(user)}
                      >
                        <UserX className="h-4 w-4 mr-1" />
                        At
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Kick Confirmation Dialog */}
      <Dialog open={kickDialogOpen} onOpenChange={setKickDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Kullanıcıyı At</DialogTitle>
            <DialogDescription>
              Bu kullanıcıyı sistemden atmak istediğinizden emin misiniz?
            </DialogDescription>
          </DialogHeader>
          {selectedUser && (
            <div className="py-4">
              <div className="bg-gray-50 rounded p-4 space-y-2">
                <p><strong>İsim:</strong> {selectedUser.user_name}</p>
                <p><strong>Email:</strong> {selectedUser.user_email}</p>
                <p><strong>Rol:</strong> {getRoleBadge(selectedUser.user_role)}</p>
                {selectedUser.company_name && (
                  <p><strong>Firma:</strong> {selectedUser.company_name}</p>
                )}
              </div>
              <p className="text-sm text-amber-600 mt-4">
                ⚠️ Kullanıcı anında oturumu kapatılacak ve yeniden giriş yapması gerekecek.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setKickDialogOpen(false)}>
              İptal
            </Button>
            <Button variant="destructive" onClick={handleKickUser}>
              <UserX className="h-4 w-4 mr-2" />
              Kullanıcıyı At
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default ActiveUsers;
